import dayjs, { Dayjs } from "dayjs";
import { toOpsUtcBound } from "../OpsTat/OpsTatFilters";

/**
 * Request types for the Alerts monitoring dashboard
 * (`/v1/dashboards/alerts/*` on the analytics host).
 * Mirrors `analytics-server/src/routes/v1/alerts/alerts.types.ts`.
 */

export const ALERT_AUDIENCES = ["ADMIN", "CUSTOMER"] as const;
export type AlertAudience = (typeof ALERT_AUDIENCES)[number];

export const ALERT_SEVERITIES = ["HIGH", "MEDIUM", "LOW"] as const;
export type AlertSeverityCode = (typeof ALERT_SEVERITIES)[number];

export const ALERT_STATUSES = ["NONE", "OPEN", "RESOLVED"] as const;
export type AlertStatusCode = (typeof ALERT_STATUSES)[number];

/** `alert_deliveries.status` for admin push rows. */
export const PUSH_STATUSES = ["PENDING", "SENT", "FAILED", "SKIPPED"] as const;
export type PushStatus = (typeof PUSH_STATUSES)[number];

/** `customer_alert_email_batches.status` (one email per client user). */
export const EMAIL_BATCH_STATUSES = [
  "QUEUED",
  "SENT",
  "DELIVERED",
  "BOUNCED",
  "FAILED",
  "SKIPPED",
] as const;
export type EmailBatchStatus = (typeof EMAIL_BATCH_STATUSES)[number];

export const RECIPIENT_KINDS = ["ADMIN", "CUSTOMER_USER"] as const;
export type RecipientKind = (typeof RECIPIENT_KINDS)[number];

export type AlertsSortDir = "asc" | "desc";

export interface AlertsSort {
  column: string;
  dir: AlertsSortDir;
}

/** `AlertFilters` — summary, drilldown and recipient bodies. Dates are UTC `YYYY-MM-DD HH:mm:ss`. */
export interface AlertFilters {
  start_date: string;
  end_date: string;
  audience?: AlertAudience | null;
  section_codes?: string[];
  type_codes?: string[];
  severities?: AlertSeverityCode[];
  customer_ids?: number[];
  statuses?: AlertStatusCode[];
}

/** `PagedExtras` — drilldown / emails / push / recipient. */
export interface AlertsPagedExtras {
  page?: number;
  page_size?: number;
  sort?: AlertsSort;
  format?: "json" | "csv";
}

/** Alert-grain filters without `statuses` (list endpoints rename it to `alert_statuses`). */
export type AlertBaseFilters = Omit<AlertFilters, "statuses">;

/** `POST /emails` body. `statuses` = email (batch) statuses. */
export interface AlertsEmailListRequest
  extends AlertBaseFilters, AlertsPagedExtras {
  statuses?: EmailBatchStatus[];
  alert_statuses?: AlertStatusCode[];
  user_ids?: number[];
}

/** `POST /push` body. `statuses` = push delivery statuses. */
export interface AlertsPushListRequest
  extends AlertBaseFilters, AlertsPagedExtras {
  statuses?: PushStatus[];
  alert_statuses?: AlertStatusCode[];
  admin_ids?: number[];
}

/** `POST /drilldown/:metricId` body. */
export type AlertsDrilldownRequest = AlertFilters & AlertsPagedExtras;

/** `POST /recipient` body. */
export interface AlertsRecipientRequest
  extends AlertFilters, AlertsPagedExtras {
  kind: RecipientKind;
  id: number;
}

/** Filters held by the page (local calendar days; converted to UTC on send). */
export interface AlertsAppliedFilters {
  start_date: Dayjs;
  end_date: Dayjs;
  audience: AlertAudience | null;
  section_codes: string[];
  type_codes: string[];
  severities: AlertSeverityCode[];
  customer_ids: number[];
  statuses: AlertStatusCode[];
}

/** Drill-down / list rows per page. Fixed — admins can't change it. */
export const ALERTS_PAGE_SIZE = 100;

/** Sort whitelists, per endpoint (contract §2, §4, §6, §7). */
export const ALERTS_DRILLDOWN_SORT = [
  "alert_id",
  "created_at",
  "audience",
  "type_code",
  "section_code",
  "severity",
  "customer_name",
  "ref_display",
  "status",
  "occurrence_count",
  "group_id",
  "recipients",
  "read_count",
  "unread",
  "push_sent",
  "push_failed",
  "push_skipped",
  "push_pending",
  "email_pending",
  "email_sent",
  "email_delivered",
  "email_bounced",
  "email_failed",
  "email_skipped",
] as const;

export const ALERTS_EMAILS_SORT = [
  "batch_id",
  "created_at",
  "customer_name",
  "user_name",
  "to_email",
  "status",
  "attempts",
  "alerts",
  "sent_at",
  "delivered_at",
  "failed_at",
] as const;

export const ALERTS_PUSH_SORT = [
  "delivery_id",
  "created_at",
  "alert_id",
  "type_code",
  "severity",
  "admin_name",
  "status",
  "attempts",
  "devices_tried",
  "sent_at",
] as const;

const RECIPIENT_COMMON_SORT = [
  "alert_id",
  "created_at",
  "type_code",
  "section_code",
  "severity",
  "status",
  "occurrence_count",
  "read_at",
  "archived_at",
] as const;

export const ALERTS_RECIPIENT_SORT: Record<RecipientKind, readonly string[]> = {
  ADMIN: [...RECIPIENT_COMMON_SORT, "reason", "push_status", "push_sent_at"],
  CUSTOMER_USER: [
    ...RECIPIENT_COMMON_SORT,
    "in_app",
    "email_status",
    "email_sent_at",
    "email_delivered_at",
  ],
};

export const defaultAlertsAppliedFilters = (): AlertsAppliedFilters => ({
  start_date: dayjs().subtract(29, "day").startOf("day"),
  end_date: dayjs().endOf("day"),
  audience: null,
  section_codes: [],
  type_codes: [],
  severities: [],
  customer_ids: [],
  statuses: [],
});

/** Base alert filters (no alert-status key). Empty arrays / null are dropped. */
const toAlertBaseFilters = (
  applied: AlertsAppliedFilters,
): AlertBaseFilters => {
  const body: AlertBaseFilters = {
    start_date: toOpsUtcBound(applied.start_date, "start"),
    end_date: toOpsUtcBound(applied.end_date, "end"),
  };
  if (applied.audience) {
    body.audience = applied.audience;
  }
  if (applied.section_codes.length > 0) {
    body.section_codes = applied.section_codes;
  }
  if (applied.type_codes.length > 0) {
    body.type_codes = applied.type_codes;
  }
  if (applied.severities.length > 0) {
    body.severities = applied.severities;
  }
  if (applied.customer_ids.length > 0) {
    body.customer_ids = applied.customer_ids;
  }
  return body;
};

/** Summary / drilldown / recipient body: local calendar days → UTC bounds (end = end of day). */
export const toAlertFilters = (applied: AlertsAppliedFilters): AlertFilters => {
  try {
    const body: AlertFilters = toAlertBaseFilters(applied);
    if (applied.statuses.length > 0) {
      body.statuses = applied.statuses;
    }
    return body;
  } catch (error) {
    throw error;
  }
};

/**
 * `/emails` and `/push` body base: the summary's `statuses` move to `alert_statuses`
 * (contract: "Status on list endpoints").
 */
export const toAlertListFilters = (
  applied: AlertsAppliedFilters,
): AlertBaseFilters & { alert_statuses?: AlertStatusCode[] } => {
  try {
    const body: AlertBaseFilters & { alert_statuses?: AlertStatusCode[] } =
      toAlertBaseFilters(applied);
    if (applied.statuses.length > 0) {
      body.alert_statuses = applied.statuses;
    }
    return body;
  } catch (error) {
    throw error;
  }
};
